if (!customElements.get('premium-product')) {
  customElements.define(
    'premium-product',
    class PremiumProduct extends HTMLElement {
      connectedCallback() {
        const data = this.querySelector('[data-variants]');
        this.variants = data ? JSON.parse(data.textContent) : [];
        this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
        this.variantInput = this.querySelector('[data-variant-input]');
        this.addButton = this.querySelector('[data-add-button]');

        this.initGallery();
        this.initOptions();
        this.initQuantity();
        this.initBuyNow();
        this.initDelivery();
        this.initCopy();
        this.initFbt();
        this.initSticky();
      }

      /* ---------- Helpers ---------- */
      formatMoney(cents) {
        const value = (Number(cents) || 0) / 100;
        const withDelimiters = (number, decimals, thousands = ',', decimal = '.') => {
          const [whole, fraction] = number.toFixed(decimals).split('.');
          const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return fraction ? grouped + decimal + fraction : grouped;
        };
        return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
          switch (key) {
            case 'amount_no_decimals':
              return withDelimiters(value, 0);
            case 'amount_with_comma_separator':
              return withDelimiters(value, 2, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return withDelimiters(value, 0, '.', ',');
            case 'amount_with_apostrophe_separator':
              return withDelimiters(value, 2, "'", '.');
            default:
              return withDelimiters(value, 2);
          }
        });
      }

      toast(message) {
        const toast = this.querySelector('[data-toast]');
        if (!toast) return;
        toast.textContent = message;
        toast.hidden = false;
        clearTimeout(this.toastTimer);
        this.toastTimer = setTimeout(() => (toast.hidden = true), 2600);
      }

      async refreshCartBubble() {
        try {
          const response = await fetch(`${window.routes?.root_url || '/'}?sections=cart-icon-bubble`);
          const sections = await response.json();
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble && sections['cart-icon-bubble']) {
            const html = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
            const source = html.querySelector('.shopify-section') || html.body;
            bubble.innerHTML = source.innerHTML;
          }
        } catch (error) {
          /* the cart still updated; the counter refreshes on next page load */
        }
      }

      /* ---------- Gallery ---------- */
      initGallery() {
        this.slides = this.querySelector('[data-slides]');
        this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
        this.dots = Array.from(this.querySelectorAll('.pdp__dot'));
        if (!this.slides) return;

        this.thumbs.forEach((thumb) =>
          thumb.addEventListener('click', () => this.goToSlide(parseInt(thumb.dataset.thumb, 10)))
        );

        let ticking = false;
        this.slides.addEventListener(
          'scroll',
          () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
              const index = Math.round(this.slides.scrollLeft / this.slides.clientWidth);
              this.setActiveThumb(index);
              ticking = false;
            });
          },
          { passive: true }
        );
      }

      goToSlide(index) {
        if (!this.slides) return;
        this.slides.scrollTo({ left: index * this.slides.clientWidth, behavior: 'smooth' });
        this.setActiveThumb(index);
      }

      goToMedia(mediaId) {
        if (!mediaId || !this.slides) return;
        const slides = Array.from(this.slides.children);
        const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index > -1) this.goToSlide(index);
      }

      setActiveThumb(index) {
        this.thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
        this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        const active = this.thumbs[index];
        if (active) active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }

      /* ---------- Variants ---------- */
      initOptions() {
        this.optionSets = Array.from(this.querySelectorAll('[data-option-index]'));
        if (!this.optionSets.length) return;
        this.optionSets.forEach((set) =>
          set.addEventListener('change', () => this.onOptionChange())
        );
        this.markUnavailable();
      }

      selectedOptions() {
        return this.optionSets.map((set) => set.querySelector('input:checked')?.value);
      }

      onOptionChange() {
        const selected = this.selectedOptions();
        this.optionSets.forEach((set, i) => {
          const label = set.querySelector('[data-option-selected]');
          if (label) label.textContent = selected[i];
        });

        const variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i]));
        this.markUnavailable();
        this.updateVariant(variant);
      }

      markUnavailable() {
        const selected = this.selectedOptions();
        this.optionSets.forEach((set, index) => {
          set.querySelectorAll('.pdp__option-pill').forEach((pill) => {
            const value = pill.querySelector('input').value;
            const match = this.variants.find((v) =>
              v.options.every((optionValue, i) => (i === index ? optionValue === value : optionValue === selected[i]))
            );
            pill.classList.toggle('is-unavailable', !match || !match.available);
          });
        });
      }

      updateVariant(variant) {
        this.currentVariant = variant;
        const available = Boolean(variant && variant.available);

        if (this.variantInput) {
          this.variantInput.value = variant ? variant.id : '';
          this.variantInput.disabled = !available;
        }

        if (variant) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
          this.goToMedia(variant.media_id);
        }

        // Price
        const price = this.querySelector('[data-price]');
        const compare = this.querySelector('[data-compare]');
        const save = this.querySelector('[data-save]');
        const badge = this.querySelector('[data-discount-badge]');
        const hasSaving = variant && variant.compare_at_price > variant.price;
        const saving = hasSaving ? Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price) : 0;

        if (price && variant) price.textContent = this.formatMoney(variant.price);
        if (compare) {
          compare.hidden = !hasSaving;
          if (hasSaving) compare.textContent = this.formatMoney(variant.compare_at_price);
        }
        if (save) {
          save.hidden = !hasSaving;
          const value = save.querySelector('[data-save-value]');
          if (value) value.textContent = saving;
        }
        if (badge) {
          badge.hidden = !hasSaving;
          const value = badge.querySelector('[data-discount-value]');
          if (value) value.textContent = saving;
        }

        // Stock
        const stock = this.querySelector('[data-stock]');
        if (stock) {
          const text = stock.querySelector('[data-stock-text]');
          const threshold = parseInt(this.dataset.lowStockThreshold, 10) || 0;
          const isLow = available && variant.inventory !== null && variant.inventory > 0 && variant.inventory <= threshold;
          stock.classList.toggle('is-out', !available);
          stock.classList.toggle('is-low', isLow);
          if (text) {
            if (!available) text.textContent = this.dataset.soldOut;
            else if (isLow) text.textContent = this.dataset.lowStock.replace('[count]', variant.inventory);
            else text.textContent = this.dataset.inStock;
          }
        }

        // Buttons
        if (this.addButton) {
          this.addButton.disabled = !available;
          const text = this.addButton.querySelector('[data-add-text]');
          if (text) text.textContent = available ? this.dataset.addLabel : this.dataset.soldOut;
        }
        const buyNow = this.querySelector('[data-buy-now]');
        if (buyNow) buyNow.disabled = !available;

        // Sticky + bought together follow the chosen variant
        const stickyPrice = this.querySelector('[data-sticky-price]');
        if (stickyPrice && variant) stickyPrice.textContent = this.formatMoney(variant.price);
        const current = this.querySelector('[data-fbt-current]');
        if (current && variant) {
          current.dataset.variantId = variant.id;
          current.dataset.price = variant.price;
          current.dataset.compare = variant.compare_at_price || variant.price;
          const currentPrice = this.querySelector('[data-fbt-current-price]');
          if (currentPrice) currentPrice.textContent = this.formatMoney(variant.price);
          this.updateFbtTotal();
        }
      }

      /* ---------- Quantity ---------- */
      initQuantity() {
        const input = this.querySelector('[data-qty-input]');
        if (!input) return;
        this.querySelectorAll('[data-qty]').forEach((button) =>
          button.addEventListener('click', () => {
            const current = parseInt(input.value, 10) || 1;
            input.value = button.dataset.qty === 'plus' ? current + 1 : Math.max(1, current - 1);
          })
        );
      }

      /* ---------- Buy it now ---------- */
      initBuyNow() {
        const button = this.querySelector('[data-buy-now]');
        if (!button) return;
        button.addEventListener('click', async () => {
          const id = this.variantInput?.value;
          const quantity = parseInt(this.querySelector('[data-qty-input]')?.value, 10) || 1;
          if (!id) return;
          button.disabled = true;
          button.classList.add('is-busy');
          try {
            const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [{ id: Number(id), quantity }] }),
            });
            if (!response.ok) throw new Error((await response.json()).description);
            // window.location.href = '/checkout';
            onBuyNowClick(this);
            button.disabled = false;
            button.classList.remove('is-busy');
          } catch (error) {
            this.toast(error.message || 'Something went wrong');
            button.disabled = false;
            button.classList.remove('is-busy');
          }
        });
      }

      /* ---------- Delivery estimate ---------- */
      initDelivery() {
        const box = this.querySelector('[data-delivery]');
        if (!box) return;
        const skipSundays = box.dataset.skipWeekends === 'true';
        const addDays = (days) => {
          const date = new Date();
          let added = 0;
          while (added < days) {
            date.setDate(date.getDate() + 1);
            if (!(skipSundays && date.getDay() === 0)) added++;
          }
          return date;
        };
        const format = (date) =>
          date.toLocaleDateString(document.documentElement.lang || undefined, {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
          });
        const from = format(addDays(parseInt(box.dataset.min, 10) || 0));
        const to = format(addDays(parseInt(box.dataset.max, 10) || 0));
        const text = box.querySelector('[data-delivery-text]');
        const [before, after] = box.dataset.template.split('[dates]');
        text.textContent = '';
        text.append(before || '');
        const strong = document.createElement('strong');
        strong.textContent = `${from} – ${to}`;
        text.append(strong, after || '');
      }

      /* ---------- Copy coupon ---------- */
      initCopy() {
        this.querySelectorAll('[data-copy]').forEach((button) =>
          button.addEventListener('click', async () => {
            try {
              await navigator.clipboard.writeText(button.dataset.copy);
            } catch (error) {
              /* ignore, still show the message */
            }
            this.toast(button.dataset.copied || button.dataset.copy);
          })
        );
      }

      /* ---------- Frequently bought together ---------- */
      async initFbt() {
        this.fbt = this.querySelector('[data-fbt]');
        if (!this.fbt) return;

        if (this.fbt.hasAttribute('data-fbt-fetch')) {
          await this.loadRecommendations();
        }

        this.fbt.addEventListener('change', (event) => {
          if (event.target.matches('[data-fbt-check]')) this.updateFbtTotal();
        });
        this.fbt.querySelector('[data-fbt-add]')?.addEventListener('click', () => this.addFbt());
        this.updateFbtTotal();
      }

      async loadRecommendations() {
        const limit = parseInt(this.fbt.dataset.limit, 10) || 2;
        const base = `${window.routes?.product_recommendations_url || '/recommendations/products'}.json`;
        const productId = this.fbt.dataset.productId;
        let products = [];
        for (const intent of ['complementary', 'related']) {
          try {
            const response = await fetch(`${base}?product_id=${productId}&limit=${limit}&intent=${intent}`);
            const data = await response.json();
            products = (data.products || []).filter((p) => p.available);
            if (products.length) break;
          } catch (error) {
            /* try the next intent */
          }
        }
        if (!products.length) return;

        const list = this.fbt.querySelector('[data-fbt-list]');
        products.slice(0, limit).forEach((product) => {
          const variant = product.variants.find((v) => v.available) || product.variants[0];
          const image = product.featured_image
            ? `${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=120`
            : '';
          const item = document.createElement('li');
          item.className = 'pdp__fbt-item';

          const check = document.createElement('input');
          check.type = 'checkbox';
          check.checked = true;
          check.setAttribute('data-fbt-check', '');
          check.dataset.variantId = variant.id;
          check.dataset.price = variant.price;
          check.dataset.compare = variant.compare_at_price || variant.price;
          check.setAttribute('aria-label', product.title);

          const thumb = document.createElement('span');
          thumb.className = 'pdp__fbt-thumb';
          if (image) {
            const img = document.createElement('img');
            img.src = image;
            img.alt = '';
            img.loading = 'lazy';
            thumb.append(img);
          }

          const name = document.createElement('a');
          name.className = 'pdp__fbt-name';
          name.href = product.url;
          name.textContent = product.title;

          const price = document.createElement('span');
          price.className = 'pdp__fbt-price';
          price.textContent = this.formatMoney(variant.price);

          item.append(check, thumb, name, price);
          list.append(item);
        });
        this.fbt.hidden = false;
      }

      checkedFbt() {
        return Array.from(this.fbt.querySelectorAll('[data-fbt-check]')).filter((input) => input.checked);
      }

      // Cart ladder tiers from the block setting, e.g. "1999:10, 2999:15, 3999:20" (minimum in store currency : percent off)
      fbtTiers() {
        if (this.fbtLadder) return this.fbtLadder;
        this.fbtLadder = (this.fbt.dataset.ladder || '')
          .split(',')
          .map((part) => part.split(':').map((value) => parseFloat(value.replace(/[^\d.]/g, ''))))
          .filter(([min, percent]) => min > 0 && percent > 0)
          .map(([min, percent]) => ({ min: Math.round(min * 100), percent }))
          .sort((a, b) => a.min - b.min);
        return this.fbtLadder;
      }

      updateFbtTotal() {
        if (!this.fbt) return;
        const checked = this.checkedFbt();
        const total = checked.reduce((sum, input) => sum + Number(input.dataset.price), 0);
        const compare = checked.reduce((sum, input) => sum + Number(input.dataset.compare), 0);

        // Same rule as the cart: the highest tier whose minimum the total reaches
        const tiers = this.fbtTiers();
        const tier = tiers.filter((t) => total >= t.min).pop();
        const next = tiers.find((t) => total < t.min);
        const final = tier ? total - Math.round((total * tier.percent) / 100) : total;
        const was = Math.max(compare, total);

        const totalEl = this.fbt.querySelector('[data-fbt-total]');
        const compareEl = this.fbt.querySelector('[data-fbt-compare]');
        if (totalEl) totalEl.textContent = this.formatMoney(final);
        if (compareEl) compareEl.textContent = was > final ? this.formatMoney(was) : '';

        const ladderEl = this.fbt.querySelector('[data-fbt-ladder]');
        if (ladderEl) {
          ladderEl.hidden = !tier;
          if (tier) ladderEl.textContent = (this.fbt.dataset.ladderLabel || '').replace('[percent]', tier.percent);
        }
        const noteEl = this.fbt.querySelector('[data-fbt-note]');
        if (noteEl) {
          const message = this.fbt.dataset.ladderNudge || '';
          noteEl.hidden = !(next && message && checked.length);
          if (!noteEl.hidden) {
            noteEl.textContent = message
              .replace('[amount]', this.formatMoney(next.min - total))
              .replace('[percent]', next.percent);
          }
        }
        const button = this.fbt.querySelector('[data-fbt-add]');
        if (button) {
          button.textContent = this.fbt.dataset.addAll.replace('[count]', checked.length).replace(/\s+/g, ' ').trim();
          button.disabled = !checked.length;
        }
      }

      async addFbt() {
        const button = this.fbt.querySelector('[data-fbt-add]');
        const items = this.checkedFbt().map((input) => ({ id: Number(input.dataset.variantId), quantity: 1 }));
        if (!items.length) return;
        button.disabled = true;
        try {
          const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description);
          const drawer = document.querySelector('cart-drawer');
          if (drawer) {
            // Open the side cart with the new items
            const ids = drawer.getSectionsToRender().map((section) => section.id);
            const sections = await (await fetch(`${window.routes?.root_url || '/'}?sections=${ids.join(',')}`)).json();
            drawer.classList.remove('is-empty');
            drawer.renderContents({ id: null, sections });
          } else {
            this.toast(`${items.length} × added to cart`);
            await this.refreshCartBubble();
          }
          // Dawn declares these as script-level globals, not window properties.
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'premium-fbt', cartData: data });
          }
        } catch (error) {
          this.toast(error.message || 'Something went wrong');
        } finally {
          button.disabled = false;
        }
      }

      /* ---------- Sticky add to cart ---------- */
      initSticky() {
        const bar = this.querySelector('[data-sticky]');
        if (!bar || !this.addButton) return;
        bar.hidden = false;
        const observer = new IntersectionObserver(
          ([entry]) => {
            const above = entry.boundingClientRect.top < 0;
            bar.classList.toggle('is-visible', !entry.isIntersecting && above);
          },
          { threshold: 0 }
        );
        observer.observe(this.addButton);
        bar.querySelector('[data-sticky-add]')?.addEventListener('click', () => {
          if (this.addButton.disabled) {
            this.addButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
          this.addButton.click();
        });
      }
    }
  );
}